import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import './AIChatbot.scss';
import { useChatbotSSE } from '../../hooks/useChatbotSSE';
import { chatbotAPI } from '../../services/apis/chatbot.api';
import { extractErrorMessage } from '../../utils/errorUtils';

interface ChatMessage {
    id: string;
    role: 'user' | 'assistant' | 'system';
    content: string;
    timestamp: Date;
    requiresConfirmation?: boolean;
    pendingAction?: {
        type: 'invite_user' | 'schedule_audit' | 'query_data' | 'system_info';
        parameters: any;
        confirmationMessage: string;
    };
}

interface ChatbotProps {
    isOpen: boolean;
    onClose: () => void;
    className?: string;
}

export const AIChatbot: React.FC<ChatbotProps> = ({ isOpen, onClose, className = '' }) => {
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [inputMessage, setInputMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [sessionId, setSessionId] = useState<string>('');
    const [isTyping, setIsTyping] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isFullWindow, setIsFullWindow] = useState(false);
    const [isConfirming, setIsConfirming] = useState(false);
    const [lastActionStatus, setLastActionStatus] = useState<'idle' | 'processing' | 'success' | 'error'>('idle');

    // State for input styling
    const [isInputFocused, setIsInputFocused] = useState(false);
    const [isInputHovered, setIsInputHovered] = useState(false);
    const [isSendButtonHovered, setIsSendButtonHovered] = useState(false);
    const [isClearButtonHovered, setIsClearButtonHovered] = useState(false);

    const messagesEndRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLTextAreaElement>(null);

    const { user } = useSelector((state: RootState) => state.auth);
    const selectedOrganization = useSelector((state: RootState) => state.organization.selectedOrganization);
    const organizationId = selectedOrganization?._id;
    const userId = user?._id;

    // Responsive design helper
    const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;

    // Inline styles for input components
    const inputContainerStyle: React.CSSProperties = {
        padding: isMobile ? '16px' : '20px',
        borderTop: '1px solid #e5e7eb',
        background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
        borderRadius: '0 0 16px 16px',
        position: 'relative'
    };

    const inputContainerBeforeStyle: React.CSSProperties = {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '1px',
        background: 'linear-gradient(90deg, transparent, #e2e8f0, transparent)',
        pointerEvents: 'none'
    };

    const inputWrapperContainerStyle: React.CSSProperties = {
        display: 'flex',
        gap: '12px',
        alignItems: 'flex-end',
        position: 'relative'
    };

    const getInputWrapperStyle = (): React.CSSProperties => ({
        flex: 1,
        position: 'relative',
        background: 'white',
        border: '2px solid #e2e8f0',
        borderColor: isInputFocused ? '#667eea' : isInputHovered ? '#cbd5e1' : '#e2e8f0',
        borderRadius: isMobile ? '16px' : '20px',
        display: 'flex',
        alignItems: 'flex-start',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        boxShadow: isInputFocused
            ? '0 0 0 4px rgba(102, 126, 234, 0.1), 0 8px 25px rgba(102, 126, 234, 0.15)'
            : isInputHovered
                ? '0 6px 16px rgba(0, 0, 0, 0.08)'
                : '0 4px 12px rgba(0, 0, 0, 0.05)',
        transform: isInputFocused ? 'translateY(-2px)' : isInputHovered ? 'translateY(-1px)' : 'translateY(0)',
        overflow: 'hidden'
    });

    const inputIconStyle: React.CSSProperties = {
        padding: isMobile ? '14px 12px 0 12px' : '16px 16px 0 16px',
        display: 'flex',
        alignItems: 'flex-start',
        color: '#64748b',
        fontSize: '18px',
        transition: 'all 0.3s ease'
    };

    const messageIconStyle: React.CSSProperties = {
        display: 'block',
        animation: 'iconFloat 3s ease-in-out infinite'
    };

    const inputFieldStyle: React.CSSProperties = {
        flex: 1,
        padding: isMobile ? '14px 6px' : '16px 8px',
        border: 'none',
        background: 'transparent',
        fontSize: isMobile ? '14px' : '15px',
        color: '#1e293b',
        outline: 'none',
        fontWeight: 500,
        minHeight: '24px',
        maxHeight: '120px',
        resize: 'none' as const,
        fontFamily: 'inherit',
        lineHeight: '1.5'
    };

    const inputActionsStyle: React.CSSProperties = {
        display: 'flex',
        alignItems: 'flex-start',
        gap: '8px',
        padding: isMobile ? '8px 8px 0 0' : '12px 12px 0 0'
    };

    const characterCounterStyle: React.CSSProperties = {
        fontSize: '11px',
        color: inputMessage.length > 1800 ? '#dc2626' : '#64748b',
        fontWeight: 600,
        padding: '4px 8px',
        background: '#f1f5f9',
        borderRadius: '8px',
        transition: 'all 0.3s ease',
        animation: inputMessage.length > 1800 ? 'warningPulse 1s ease-in-out infinite' : undefined
    };

    const getClearButtonStyle = (): React.CSSProperties => ({
        width: '24px',
        height: '24px',
        border: 'none',
        background: isClearButtonHovered ? '#e2e8f0' : '#f1f5f9',
        color: isClearButtonHovered ? '#475569' : '#64748b',
        borderRadius: '50%',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '12px',
        transition: 'all 0.2s ease',
        opacity: isClearButtonHovered ? 1 : 0.7,
        transform: isClearButtonHovered ? 'scale(1.1)' : 'scale(1)'
    });

    const getSendButtonStyle = (): React.CSSProperties => ({
        width: isMobile ? '44px' : '48px',
        height: isMobile ? '44px' : '48px',
        background: isLoading || isConfirming
            ? 'linear-gradient(135deg, #94a3b8 0%, #64748b 100%)'
            : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        border: 'none',
        borderRadius: '16px',
        color: 'white',
        cursor: isLoading || isConfirming || !inputMessage.trim() || !organizationId ? 'not-allowed' : 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '18px',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        boxShadow: isSendButtonHovered && !isLoading && !isConfirming && inputMessage.trim() && organizationId
            ? '0 8px 25px rgba(102, 126, 234, 0.4), 0 4px 12px rgba(102, 126, 234, 0.3)'
            : '0 4px 12px rgba(102, 126, 234, 0.3), 0 2px 4px rgba(102, 126, 234, 0.2)',
        transform: isSendButtonHovered && !isLoading && !isConfirming && inputMessage.trim() && organizationId
            ? 'translateY(-2px)' : 'translateY(0)',
        position: 'relative',
        overflow: 'hidden',
        opacity: isLoading || isConfirming || !inputMessage.trim() || !organizationId ? 0.5 : 1,
        animation: isLoading || isConfirming ? 'buttonPulse 1.5s ease-in-out infinite' : undefined
    });

    const sendIconStyle: React.CSSProperties = {
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    };

    const arrowStyle: React.CSSProperties = {
        position: 'relative',
        zIndex: 2,
        transition: 'all 0.3s ease',
        transform: isSendButtonHovered && !isLoading && !isConfirming && inputMessage.trim() && organizationId
            ? 'translateX(2px) scale(1.1)' : 'translateX(0) scale(1)'
    };

    const sendBgStyle: React.CSSProperties = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        width: isSendButtonHovered && !isLoading && !isConfirming && inputMessage.trim() && organizationId ? '40px' : '0',
        height: isSendButtonHovered && !isLoading && !isConfirming && inputMessage.trim() && organizationId ? '40px' : '0',
        background: 'rgba(255, 255, 255, 0.2)',
        borderRadius: '50%',
        transform: 'translate(-50%, -50%)',
        transition: 'all 0.3s ease'
    };

    const loadingSpinnerStyle: React.CSSProperties = {
        width: '18px',
        height: '18px',
        border: '2px solid rgba(255, 255, 255, 0.3)',
        borderRadius: '50%',
        borderTopColor: 'white',
        animation: 'spin 1s linear infinite'
    };

    const inputHelpStyle: React.CSSProperties = {
        marginTop: '12px',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px'
    };

    const helpContentStyle: React.CSSProperties = {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: '12px',
        color: '#64748b',
        fontWeight: 500,
        background: 'rgba(255, 255, 255, 0.7)',
        padding: '10px 16px',
        borderRadius: '12px',
        backdropFilter: 'blur(8px)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        transition: 'all 0.3s ease'
    };

    const helpIconStyle: React.CSSProperties = {
        fontSize: '14px',
        animation: 'helpIconGlow 3s ease-in-out infinite'
    };

    const helpTextStyle: React.CSSProperties = {
        lineHeight: 1.5
    };

    const inputShortcutsStyle: React.CSSProperties = {
        display: isMobile ? 'none' : 'flex', // Hide shortcuts on mobile
        gap: '12px',
        justifyContent: 'center',
        alignItems: 'center'
    };

    const shortcutStyle: React.CSSProperties = {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        fontSize: '11px',
        color: '#64748b',
        fontWeight: 500
    };

    const kbdStyle: React.CSSProperties = {
        background: '#f1f5f9',
        border: '1px solid #e2e8f0',
        borderRadius: '4px',
        padding: '2px 6px',
        fontSize: '10px',
        fontFamily: 'monospace',
        color: '#475569',
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)'
    };

    // Auto-resize textarea function
    const adjustTextareaHeight = useCallback(() => {
        if (inputRef.current) {
            inputRef.current.style.height = 'auto';
            inputRef.current.style.height = `${Math.min(inputRef.current.scrollHeight, 120)}px`;
        }
    }, []);

    // Generate sessionId upfront to avoid race conditions
    useEffect(() => {
        if (isOpen && !sessionId) {
            const newSessionId = `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
            setSessionId(newSessionId);
            console.log('🆔 [AIChatbot] Generated new session ID:', newSessionId);
        }
    }, [isOpen, sessionId]);

    // Initialize SSE connection
    const { status, lastMessage } = useChatbotSSE({
        userId,
        organizationId,
        sessionId,
        enabled: isOpen && !!userId && !!organizationId && !!sessionId
    });

    // Auto-hide success toast after 5 seconds
    useEffect(() => {
        if (lastActionStatus === 'success') {
            const timer = setTimeout(() => {
                setLastActionStatus('idle');
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [lastActionStatus]);

    // Debug function to monitor SSE connection and state
    const debugChatbotState = useCallback(() => {
        const debugInfo = {
            sessionId,
            userId,
            organizationId,
            sseStatus: status,
            messagesCount: messages.length,
            lastActionStatus,
            isLoading,
            isTyping,
            isConfirming,
            error,
            lastMessageTime: messages.length > 0 ? messages[messages.length - 1].timestamp : null,
            recentMessages: messages.slice(-3).map(msg => ({
                role: msg.role,
                content: msg.content.substring(0, 50) + '...',
                containsSuccess: msg.content.includes('✅'),
                timestamp: msg.timestamp
            }))
        };

        console.log('🔍 [AIChatbot] Debug State:', debugInfo);

        // Check if we have any success messages in the recent messages
        const hasSuccessMessage = messages.some(msg => msg.content.includes('✅'));
        if (hasSuccessMessage) {
            console.log('✅ [AIChatbot] SUCCESS MESSAGES FOUND IN CHAT HISTORY');
        } else {
            console.log('❌ [AIChatbot] NO SUCCESS MESSAGES FOUND IN CHAT HISTORY');
        }

        return debugInfo;
    }, [sessionId, userId, organizationId, status, messages, lastActionStatus, isLoading, isTyping, isConfirming, error]);

    // Add debug logging when key states change
    useEffect(() => {
        if (process.env.NODE_ENV === 'development') {
            debugChatbotState();
        }
    }, [sessionId, status, lastActionStatus, debugChatbotState]);

    // Enhanced SSE message handling with better success feedback
    const handleSSEMessage = useCallback((message: any) => {
        console.log('🔵 [AIChatbot] Received SSE message:', message);

        try {
            if (message.type === 'chat_message') {
                const messageData = message.data;

                console.log('📨 [AIChatbot] Processing chat message:', {
                    role: messageData.message?.role,
                    content: messageData.message?.content?.substring(0, 100) + '...',
                    containsSuccessEmoji: messageData.message?.content?.includes('✅'),
                    requiresConfirmation: messageData.requiresConfirmation,
                    sessionId: messageData.sessionId,
                    currentSessionId: sessionId,
                    userId: messageData.userId,
                    organizationId: messageData.organizationId
                });

                // Improved session validation - accept if user/org matches
                const isValidMessage = (
                    messageData.userId === userId &&
                    messageData.organizationId === organizationId
                );

                if (!isValidMessage) {
                    console.warn('⚠️ [AIChatbot] Message not for current user/organization, ignoring');
                    return;
                }

                // Update session ID if it's different but message is for same user/org
                if (messageData.sessionId && messageData.sessionId !== sessionId) {
                    console.log('🔄 [AIChatbot] Updating session ID from SSE message:', {
                        old: sessionId,
                        new: messageData.sessionId
                    });
                    setSessionId(messageData.sessionId);
                }

                if (messageData.message) {
                    const newMessage: ChatMessage = {
                        id: `msg-${Date.now()}-${Math.random()}`,
                        role: messageData.message.role,
                        content: messageData.message.content,
                        timestamp: new Date(messageData.message.timestamp),
                        requiresConfirmation: messageData.requiresConfirmation,
                        pendingAction: messageData.pendingAction
                    };

                    // Enhanced success message tracking
                    if (newMessage.content.includes('✅') && newMessage.role === 'assistant') {
                        console.log('✅ [AIChatbot] SUCCESS MESSAGE DETECTED!', {
                            content: newMessage.content,
                            hasSuccessEmoji: true,
                            willShowSuccessStyle: true,
                            timestamp: new Date().toISOString()
                        });

                        // Only update status to success, but don't clear processing indicators yet
                        // They will be cleared after the message is fully processed
                        setLastActionStatus('success');
                    }

                    // Check for duplicate messages and handle success message replacement
                    setMessages(prev => {
                        const isDuplicate = prev.some(msg =>
                            msg.content === newMessage.content &&
                            msg.role === newMessage.role &&
                            Math.abs(msg.timestamp.getTime() - newMessage.timestamp.getTime()) < 5000
                        );

                        if (isDuplicate) {
                            console.log('⚠️ [AIChatbot] Skipping duplicate message');
                            return prev;
                        }

                        // If this is a success message, remove any temporary processing messages
                        let updatedMessages = [...prev];
                        if (newMessage.content.includes('✅') && newMessage.role === 'assistant') {
                            // Remove temporary confirmation messages when success arrives
                            updatedMessages = updatedMessages.filter(msg => {
                                const isTemporaryMessage = (
                                    msg.content.includes('⏳ **Processing your confirmation...**') ||
                                    msg.content.includes('🔧 **Testing SSE connection**')
                                );

                                if (isTemporaryMessage) {
                                    console.log('🗑️ [AIChatbot] Removing temporary message:', msg.content.substring(0, 50) + '...');
                                    return false;
                                }
                                return true;
                            });

                            console.log('✅ [AIChatbot] SUCCESS MESSAGE will be added to chat with green styling!');
                        }

                        console.log('✅ [AIChatbot] Adding new message to chat', {
                            messageRole: newMessage.role,
                            messageLength: newMessage.content.length,
                            containsSuccess: newMessage.content.includes('✅'),
                            messagesAfterCleanup: updatedMessages.length,
                            timestamp: new Date().toISOString()
                        });

                        return [...updatedMessages, newMessage];
                    });

                    // Clear error since we received a valid message
                    setError(null);

                    // Only clear processing states for completion messages (success/error)
                    if (newMessage.content.includes('✅') && newMessage.role === 'assistant') {
                        // Success message - clear all processing states
                        setIsLoading(false);
                        setIsTyping(false);
                        setIsConfirming(false);
                        setLastActionStatus('success');
                        console.log('✅ [AIChatbot] SUCCESS STATUS SET - detailed message should now appear in chat with green styling');
                    } else if (newMessage.content.includes('❌') && newMessage.role === 'assistant') {
                        // Error message - clear all processing states
                        setIsLoading(false);
                        setIsTyping(false);
                        setIsConfirming(false);
                        setLastActionStatus('error');
                        console.log('❌ [AIChatbot] Error message detected');
                    }
                    // For regular messages, keep processing states active until explicit completion
                }
            } else if (message.type === 'mcp_operation') {
                const operationData = message.data;

                console.log('🔧 [AIChatbot] Processing MCP operation:', {
                    operationType: operationData.operationType,
                    status: operationData.status,
                    sessionId: operationData.sessionId,
                    currentSessionId: sessionId,
                    userId: operationData.userId,
                    organizationId: operationData.organizationId
                });

                // Improved validation for MCP operations
                const isValidOperation = (
                    operationData.userId === userId &&
                    operationData.organizationId === organizationId
                );

                if (!isValidOperation) {
                    console.warn('⚠️ [AIChatbot] MCP operation not for current user/organization, ignoring');
                    return;
                }

                // Handle MCP operation completion
                if (operationData.status === 'completed') {
                    setLastActionStatus('success');
                    setIsLoading(false);
                    setIsTyping(false);
                    setIsConfirming(false);
                    console.log('✅ [AIChatbot] MCP operation completed successfully', {
                        operationType: operationData.operationType,
                        result: operationData.result,
                        timestamp: new Date().toISOString()
                    });
                } else if (operationData.status === 'failed') {
                    setLastActionStatus('error');
                    setIsLoading(false);
                    setIsTyping(false);
                    setIsConfirming(false);
                    console.log('❌ [AIChatbot] MCP operation failed', {
                        operationType: operationData.operationType,
                        error: operationData.error
                    });
                } else if (operationData.status === 'processing') {
                    // Keep processing states active for ongoing operations
                    setLastActionStatus('processing');
                    setIsLoading(true);
                    console.log('🔄 [AIChatbot] MCP operation processing...', {
                        operationType: operationData.operationType,
                        timestamp: new Date().toISOString()
                    });
                }
            } else if (message.type === 'chat_status') {
                const statusData = message.data;

                console.log('📊 [AIChatbot] Processing chat status:', {
                    status: statusData.status,
                    sessionId: statusData.sessionId,
                    currentSessionId: sessionId,
                    userId: statusData.userId,
                    organizationId: statusData.organizationId
                });

                // Improved validation for chat status
                const isValidStatus = (
                    statusData.userId === userId &&
                    statusData.organizationId === organizationId
                );

                if (!isValidStatus) {
                    console.warn('⚠️ [AIChatbot] Chat status not for current user/organization, ignoring');
                    return;
                }

                switch (statusData.status) {
                    case 'typing':
                        setIsTyping(true);
                        setIsLoading(false);
                        break;
                    case 'processing':
                        setIsLoading(true);
                        setIsTyping(false);
                        setLastActionStatus('processing');
                        break;
                    case 'idle':
                        setIsLoading(false);
                        setIsTyping(false);
                        setIsConfirming(false);
                        break;
                    case 'error':
                        setIsLoading(false);
                        setIsTyping(false);
                        setIsConfirming(false);
                        setLastActionStatus('error');
                        setError(statusData.message || 'An error occurred');
                        break;
                }
            }
        } catch (error) {
            console.error('❌ [AIChatbot] Error processing SSE message:', error);
            setError('Error processing message');
        }
    }, [sessionId, userId, organizationId]);

    // Handle SSE messages
    useEffect(() => {
        if (lastMessage) {
            handleSSEMessage(lastMessage);
        }
    }, [lastMessage, handleSSEMessage]);

    const getMessageClass = (message: ChatMessage) => {
        let className = `message ${message.role}`;

        // Add special styling for success and error messages
        if (message.role === 'assistant') {
            if (message.content.includes('✅')) {
                className += ' success-message';
            } else if (message.content.includes('❌')) {
                className += ' error-message-content';
            }
        }

        return className;
    };

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    useEffect(() => {
        if (isOpen && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isOpen]);

    // Auto-resize textarea when input changes
    useEffect(() => {
        adjustTextareaHeight();
    }, [inputMessage, adjustTextareaHeight]);

    const toggleFullWindow = () => {
        setIsFullWindow(prev => !prev);
    };

    const sendMessage = async () => {
        if (!inputMessage.trim() || isLoading || isConfirming) return;

        if (!organizationId) {
            setError('Please select an organization before using the AI chatbot');
            return;
        }

        const messageToSend = inputMessage.trim();
        setInputMessage('');
        setIsLoading(true);
        setError(null);

        // Reset textarea height
        if (inputRef.current) {
            inputRef.current.style.height = 'auto';
        }

        // Add user message immediately for better UX
        const userMessage: ChatMessage = {
            id: `user-${Date.now()}`,
            role: 'user',
            content: messageToSend,
            timestamp: new Date()
        };
        setMessages(prev => [...prev, userMessage]);

        // Add immediate loading feedback
        const loadingMessage: ChatMessage = {
            id: `loading-${Date.now()}`,
            role: 'assistant',
            content: '⏳ Processing your request...',
            timestamp: new Date()
        };
        setMessages(prev => [...prev, loadingMessage]);

        try {
            console.log('📤 [AIChatbot] Sending message to backend:', {
                message: messageToSend,
                sessionId: sessionId,
                organizationId: organizationId
            });

            const response = await chatbotAPI.sendMessage({
                message: messageToSend,
                sessionId: sessionId, // Use the generated sessionId
                organizationId: organizationId!
            });

            console.log('📥 [AIChatbot] Backend response:', response);

            // Remove loading message
            setMessages(prev => prev.filter(msg => msg.id !== loadingMessage.id));

            if (response && response.data && response.data.success) {
                // Update session ID if it's different from backend
                if (response.data.data.sessionId && response.data.data.sessionId !== sessionId) {
                    console.log('🔄 [AIChatbot] Updating session ID from API response:', {
                        old: sessionId,
                        new: response.data.data.sessionId
                    });
                    setSessionId(response.data.data.sessionId);
                }

                // The actual response will come via SSE, but add fallback handling
                const aiResponse = response.data.data.response;
                const requiresConfirmation = response.data.data.requiresConfirmation;
                const pendingAction = response.data.data.pendingAction;

                if (aiResponse) {
                    // Add a short delay to allow SSE to arrive first
                    setTimeout(() => {
                        setMessages(prev => {
                            // Check if we already have this response via SSE
                            const hasResponse = prev.some(msg =>
                                msg.role === 'assistant' &&
                                msg.content === aiResponse &&
                                Math.abs(msg.timestamp.getTime() - Date.now()) < 10000 // 10 second window
                            );

                            if (!hasResponse) {
                                console.log('📝 [AIChatbot] Adding fallback API response (SSE didn\'t arrive)');
                                const assistantMessage: ChatMessage = {
                                    id: `api-${Date.now()}`,
                                    role: 'assistant',
                                    content: aiResponse,
                                    timestamp: new Date(),
                                    requiresConfirmation,
                                    pendingAction
                                };
                                return [...prev, assistantMessage];
                            } else {
                                console.log('✅ [AIChatbot] SSE response already received, skipping API fallback');
                            }
                            return prev;
                        });
                    }, 1000); // 1 second delay for SSE
                }
            } else {
                throw new Error('Invalid response structure');
            }

        } catch (error: any) {
            console.error('Error sending message:', error);

            // Remove loading message
            setMessages(prev => prev.filter(msg => msg.id !== loadingMessage.id));

            const errorMessage = extractErrorMessage(error, 'Failed to send message. Please try again.');
            setError(errorMessage);

            const systemErrorMessage: ChatMessage = {
                id: `error-${Date.now()}`,
                role: 'system',
                content: `❌ **Error:** ${errorMessage}`,
                timestamp: new Date()
            };
            setMessages(prev => [...prev, systemErrorMessage]);

            // Only clear loading state on error, let SSE handle success states
            setIsLoading(false);
            setIsTyping(false);
        }
        // Remove the finally block - let SSE messages control the loading states
    };

    const handleConfirmAction = async (confirmed: boolean, message: ChatMessage) => {
        if (!sessionId || isConfirming) {
            setError('No active session found or confirmation already in progress');
            return;
        }

        if (!organizationId) {
            setError('Please select an organization before confirming actions');
            return;
        }

        setIsConfirming(true);
        setError(null);
        setLastActionStatus('processing');

        // Add immediate feedback for the confirmation
        const confirmationFeedback: ChatMessage = {
            id: `confirm-${Date.now()}`,
            role: 'assistant',
            content: confirmed
                ? `⏳ **Processing your confirmation...**\n\n${message.pendingAction?.type === 'invite_user' ? 'Sending invitation email and setting up user account...' : 'Scheduling audit and notifying participants...'}`
                : '❌ **Action cancelled** - No changes have been made.',
            timestamp: new Date()
        };
        setMessages(prev => [...prev, confirmationFeedback]);

        try {
            console.log('📤 [AIChatbot] Confirming action:', {
                sessionId: sessionId,
                confirmed: confirmed,
                organizationId: organizationId,
                actionType: message.pendingAction?.type
            });

            const response = await chatbotAPI.confirmAction({
                sessionId,
                confirmed,
                organizationId: organizationId!
            });

            console.log('📥 [AIChatbot] Confirmation response:', response);

            // For cancelled actions, keep the cancellation message
            if (!confirmed) {
                setIsConfirming(false);
                setLastActionStatus('idle');
                return;
            }

            // For confirmed actions, remove the temporary message after a reasonable delay
            // The success message will come via SSE and will be added to the chat
            setTimeout(() => {
                setMessages(prev => {
                    // Only remove the temporary message if no success message has arrived
                    const hasSuccessMessage = prev.some(msg =>
                        msg.content.includes('✅') &&
                        msg.timestamp.getTime() > confirmationFeedback.timestamp.getTime()
                    );

                    if (!hasSuccessMessage) {
                        console.log('⚠️ [AIChatbot] Removing temporary message - no success message received yet');
                        return prev.filter(msg => msg.id !== confirmationFeedback.id);
                    } else {
                        console.log('✅ [AIChatbot] Success message found, keeping temporary message for now');
                        return prev;
                    }
                });
            }, 3000); // Wait 3 seconds

            // Response will come via SSE
            if (response && response.data && response.data.success) {
                console.log('✅ [AIChatbot] Action confirmed successfully via API');

                if (confirmed) {
                    // Don't set success status here - let SSE handle it
                    console.log('✅ [AIChatbot] Action confirmed, waiting for SSE success message');

                    // Add fallback success message if SSE doesn't deliver within 5 seconds
                    setTimeout(() => {
                        setMessages(prev => {
                            // Check if a success message has already arrived
                            const hasSuccessMessage = prev.some(msg =>
                                msg.content.includes('✅') &&
                                msg.timestamp.getTime() > confirmationFeedback.timestamp.getTime()
                            );

                            if (!hasSuccessMessage) {
                                console.log('⚠️ [AIChatbot] SSE success message not received, adding fallback message');

                                // Remove temporary message and add fallback success message
                                const filteredMessages = prev.filter(msg => msg.id !== confirmationFeedback.id);

                                const fallbackSuccessMessage: ChatMessage = {
                                    id: `fallback-success-${Date.now()}`,
                                    role: 'assistant',
                                    content: message.pendingAction?.type === 'invite_user'
                                        ? `✅ **Invitation sent successfully!**\n\nAn invitation email has been sent to the specified user to join your organization.`
                                        : `✅ **Audit scheduled successfully!**\n\nThe audit has been scheduled and participants have been notified.`,
                                    timestamp: new Date()
                                };

                                // Clear processing states as fallback
                                setIsConfirming(false);
                                setLastActionStatus('success');

                                return [...filteredMessages, fallbackSuccessMessage];
                            } else {
                                console.log('✅ [AIChatbot] SSE success message already received, no fallback needed');
                                return prev;
                            }
                        });
                    }, 5000); // Wait 5 seconds for SSE message
                }
            } else {
                throw new Error('Invalid confirmation response structure');
            }

        } catch (error: any) {
            console.error('Error confirming action:', error);

            // Remove temporary confirmation message
            setMessages(prev => prev.filter(msg => msg.id !== confirmationFeedback.id));

            let errorMessage = 'Failed to confirm action';
            if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.message) {
                errorMessage = error.message;
            }

            setError(errorMessage);
            setLastActionStatus('error');

            const errorMsg: ChatMessage = {
                id: `conf-error-${Date.now()}`,
                role: 'system',
                content: `❌ **Confirmation Error:** ${errorMessage}`,
                timestamp: new Date()
            };
            setMessages(prev => [...prev, errorMsg]);

            // Clear processing states only on error
            setIsConfirming(false);
        }
        // Let SSE messages control when to clear isConfirming for successful actions
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    const getMessageIcon = (role: string) => {
        switch (role) {
            case 'user':
                return '👤';
            case 'assistant':
                return '🤖';
            case 'system':
                return '⚙️';
            default:
                return '💬';
        }
    };

    const formatMessageContent = (content: string) => {
        return content
            .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
            .replace(/\*(.*?)\*/g, '<em>$1</em>')
            .replace(/\n/g, '<br>');
    };

    if (!isOpen) return null;

    return (
        <>
            {/* CSS Animations for Input Box */}
            <style>
                {`
                    @keyframes iconFloat {
                        0%, 100% { transform: translateY(0); }
                        50% { transform: translateY(-2px); }
                    }
                    
                    @keyframes spin {
                        from { transform: rotate(0deg); }
                        to { transform: rotate(360deg); }
                    }
                    
                    @keyframes iconSpin {
                        from { transform: rotate(0deg); }
                        to { transform: rotate(360deg); }
                    }
                    
                    @keyframes wrapperPulse {
                        0%, 100% { box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05); }
                        50% { box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08); }
                    }
                    
                    @keyframes warningPulse {
                        0%, 100% { color: #dc2626; }
                        50% { color: #ef4444; }
                    }
                    
                    @keyframes helpIconGlow {
                        0%, 100% { opacity: 0.8; }
                        50% { opacity: 1; }
                    }
                    
                    @keyframes buttonPulse {
                        0%, 100% { 
                            box-shadow: 0 4px 12px rgba(148, 163, 184, 0.3), 0 2px 4px rgba(148, 163, 184, 0.2); 
                        }
                        50% { 
                            box-shadow: 0 6px 20px rgba(148, 163, 184, 0.4), 0 3px 8px rgba(148, 163, 184, 0.3); 
                        }
                    }
                `}
            </style>

            <div className={`ai-chatbot ${className} ${isFullWindow ? 'full-window' : ''}`}>
                <div className="chatbot-header">
                    <div className="header-content">
                        <div className="chatbot-title">
                            <span className="bot-icon">🤖</span>
                            <h3>P3M AI Assistant</h3>
                            <span className={`status-indicator ${status === 'connected' ? 'connected' : 'disconnected'}`}>
                                {status === 'connected' ? '🟢' : status === 'connecting' ? '🟡' : '🔴'}
                            </span>
                            {(isLoading || isConfirming) && (
                                <span className="activity-indicator">
                                    <span className="loading-spinner"></span>
                                </span>
                            )}
                            {lastActionStatus === 'success' && (
                                <span className="success-indicator">
                                    <span className="success-checkmark">✓</span>
                                </span>
                            )}
                            {lastActionStatus === 'error' && (
                                <span className="error-indicator">
                                    <span className="error-x">✗</span>
                                </span>
                            )}
                            {lastActionStatus === 'processing' && (
                                <span className="processing-indicator">
                                    <span className="processing-dots">
                                        <span></span>
                                        <span></span>
                                        <span></span>
                                    </span>
                                </span>
                            )}
                        </div>
                        <div className="header-actions">
                            <button
                                className="window-toggle-button"
                                onClick={toggleFullWindow}
                                aria-label={isFullWindow ? 'Exit full window' : 'Enter full window'}
                                title={isFullWindow ? 'Exit full window' : 'Enter full window'}
                            >
                                {isFullWindow ? '🗗' : '⛶'}
                            </button>
                            <button className="close-button" onClick={onClose} aria-label="Close chatbot">
                                ✕
                            </button>
                        </div>
                    </div>
                    <p className="chatbot-subtitle">
                        {status === 'connected'
                            ? selectedOrganization
                                ? `AI assistant for ${selectedOrganization.name} - User management and administration`
                                : 'Select an organization to start using AI assistant'
                            : status === 'connecting'
                                ? 'Connecting to AI assistant...'
                                : 'Connection lost - trying to reconnect...'
                        }
                        {lastActionStatus === 'processing' && (
                            <span className="subtitle-status"> • Processing your request...</span>
                        )}
                        {lastActionStatus === 'error' && (
                            <span className="subtitle-status error"> • Last action failed</span>
                        )}
                    </p>
                </div>

                <div className="chatbot-messages">
                    {messages.length === 0 && (
                        <div className="welcome-message">
                            <div className="welcome-content">
                                <span className="welcome-icon">👋</span>
                                <h4>Welcome to P3M AI Assistant!</h4>
                                <p>I can help you with:</p>
                                <ul>
                                    <li>🎯 Inviting users to your organization</li>
                                    <li>📅 Scheduling audits and assessments</li>
                                    <li>📊 Getting user statistics and insights</li>
                                    <li>🏢 Organization information</li>
                                    <li>💼 Managing administrative tasks</li>
                                </ul>
                                <p><strong>Just type your request and I'll assist you!</strong></p>
                                <div className="example-queries">
                                    <small>
                                        <strong>Try:</strong> "Invite John as Admin" or "Schedule audit with Form X for Teacher Y on Jan 15"
                                    </small>
                                </div>
                            </div>
                        </div>
                    )}

                    {messages.map((message) => (
                        <div key={message.id} className={getMessageClass(message)}>
                            <div className="message-avatar">
                                <span>{getMessageIcon(message.role)}</span>
                            </div>
                            <div className="message-content">
                                <div
                                    className="message-text"
                                    dangerouslySetInnerHTML={{
                                        __html: formatMessageContent(message.content)
                                    }}
                                />
                                <div className="message-timestamp">
                                    {message.timestamp.toLocaleTimeString([], {
                                        hour: '2-digit',
                                        minute: '2-digit'
                                    })}
                                </div>

                                {message.requiresConfirmation && message.pendingAction && (
                                    <div className="confirmation-actions">
                                        <div className="confirmation-context">
                                            {message.pendingAction.type === 'invite_user' && (
                                                <span className="action-icon">👥 User Invitation</span>
                                            )}
                                            {message.pendingAction.type === 'schedule_audit' && (
                                                <span className="action-icon">📅 Audit Scheduling</span>
                                            )}
                                        </div>
                                        <div className="confirmation-buttons">
                                            <button
                                                className="confirm-button confirm"
                                                onClick={() => handleConfirmAction(true, message)}
                                                disabled={isLoading || isConfirming}
                                            >
                                                {isConfirming ? '⏳ Confirming...' : '✅ Yes, proceed'}
                                            </button>
                                            <button
                                                className="confirm-button cancel"
                                                onClick={() => handleConfirmAction(false, message)}
                                                disabled={isLoading || isConfirming}
                                            >
                                                ❌ Cancel
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}

                    {isTyping && (
                        <div className="message assistant typing">
                            <div className="message-avatar">
                                <span>🤖</span>
                            </div>
                            <div className="message-content">
                                <div className="typing-indicator">
                                    <span className="typing-text">AI is thinking...</span>
                                    <div className="typing-dots">
                                        <span></span>
                                        <span></span>
                                        <span></span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {error && (
                        <div className="error-message">
                            <span className="error-icon">⚠️</span>
                            <span>{error}</span>
                            <button onClick={() => setError(null)} className="error-close">✕</button>
                        </div>
                    )}

                    <div ref={messagesEndRef} />
                </div>

                <div style={inputContainerStyle}>
                    {/* Decorative top border */}
                    <div style={inputContainerBeforeStyle}></div>

                    <div style={inputWrapperContainerStyle}>
                        <div
                            style={getInputWrapperStyle()}
                            onMouseEnter={() => setIsInputHovered(true)}
                            onMouseLeave={() => setIsInputHovered(false)}
                        >
                            <div style={inputIconStyle}>
                                <span style={messageIconStyle}>💬</span>
                            </div>
                            <textarea
                                ref={inputRef}
                                value={inputMessage}
                                onChange={(e) => setInputMessage(e.target.value)}
                                onKeyPress={handleKeyPress}
                                onFocus={() => setIsInputFocused(true)}
                                onBlur={() => setIsInputFocused(false)}
                                placeholder={
                                    isLoading
                                        ? "Processing..."
                                        : isConfirming
                                            ? "Confirming action..."
                                            : organizationId
                                                ? "Type your message here..."
                                                : "Select an organization to start chatting..."
                                }
                                disabled={isLoading || isConfirming || !organizationId}
                                maxLength={2000}
                                style={inputFieldStyle}
                                rows={1}
                            />
                            <div style={inputActionsStyle}>
                                {inputMessage.trim() && (
                                    <div style={characterCounterStyle}>
                                        <span>
                                            {inputMessage.length}/2000
                                        </span>
                                    </div>
                                )}
                                {inputMessage.trim() && !isLoading && !isConfirming && (
                                    <button
                                        type="button"
                                        style={getClearButtonStyle()}
                                        onClick={() => {
                                            setInputMessage('');
                                            if (inputRef.current) {
                                                inputRef.current.style.height = 'auto';
                                                inputRef.current.focus();
                                            }
                                        }}
                                        onMouseEnter={() => setIsClearButtonHovered(true)}
                                        onMouseLeave={() => setIsClearButtonHovered(false)}
                                        aria-label="Clear message"
                                        title="Clear message"
                                    >
                                        <span>✕</span>
                                    </button>
                                )}
                            </div>
                        </div>
                        <button
                            onClick={sendMessage}
                            disabled={isLoading || isConfirming || !inputMessage.trim() || !organizationId}
                            style={getSendButtonStyle()}
                            onMouseEnter={() => setIsSendButtonHovered(true)}
                            onMouseLeave={() => setIsSendButtonHovered(false)}
                            aria-label="Send message"
                            title="Send message"
                        >
                            {isLoading || isConfirming ? (
                                <span style={loadingSpinnerStyle}></span>
                            ) : (
                                <div style={sendIconStyle}>
                                    <span style={arrowStyle}>➤</span>
                                    <div style={sendBgStyle}></div>
                                </div>
                            )}
                        </button>
                    </div>
                    <div style={inputHelpStyle}>
                        <div style={helpContentStyle}>
                            <span style={helpIconStyle}>💡</span>
                            <span style={helpTextStyle}>
                                {isLoading
                                    ? "Processing your request..."
                                    : isConfirming
                                        ? "Confirming your action..."
                                        : organizationId
                                            ? "Try: 'Invite John as Admin' or 'Schedule audit with Form X for Teacher Y on Jan 15'"
                                            : "Please select an organization from the sidebar to use AI assistant"}
                            </span>
                        </div>
                        {organizationId && !isLoading && !isConfirming && (
                            <div style={inputShortcutsStyle}>
                                <span style={shortcutStyle}>
                                    <span style={kbdStyle}>Enter</span> to send
                                </span>
                                <span style={shortcutStyle}>
                                    <span style={kbdStyle}>Shift</span> + <span style={kbdStyle}>Enter</span> for new line
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}; 